/**
 * Frontend client for the Report Builder backend.
 */

import { auth } from '@/config/firebase';
import { apiRequest, APIError } from './api';
import { detectBrowserTimeZone } from '@/lib/utils';
import type {
  BuilderConfig,
  BuilderQueryResponse,
  BuilderRunsQueryResponse,
  ReportBuilderCatalog,
  SavedReport,
  SavedReportInput,
} from '@simcheck/shared-types';
import { API_URL } from '@/config/env';

const API_BASE_URL = API_URL;

export async function fetchCatalog(): Promise<ReportBuilderCatalog> {
  return apiRequest<ReportBuilderCatalog>(
    '/reports/catalog',
    { method: 'GET' },
  );
}

export async function runQuery(
  organizationId: string,
  config: BuilderConfig,
  timeZone: string = detectBrowserTimeZone(),
): Promise<BuilderQueryResponse> {
  return apiRequest<BuilderQueryResponse>(
    `/reports/query?tz=${encodeURIComponent(timeZone)}`,
    { method: 'POST', body: JSON.stringify(config) },
    organizationId,
  );
}

/**
 * Download the current report. Hits the same `/reports/query` endpoint with
 * the requested `format` and triggers a same-tab anchor-click download.
 */
export async function exportReport(
  organizationId: string,
  config: BuilderConfig,
  format: 'csv' | 'pdf',
  filename: string,
  timeZone: string = detectBrowserTimeZone(),
): Promise<void> {
  const token = await auth.currentUser?.getIdToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Organization-Id': organizationId,
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE_URL}/reports/query?format=${format}&tz=${encodeURIComponent(timeZone)}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(config),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new APIError(text || res.statusText, res.status);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    a.remove();
    URL.revokeObjectURL(url);
  }, 100);
}

/** @deprecated Use exportReport(orgId, config, 'csv', filename) instead. */
export async function exportReportCsv(
  organizationId: string,
  config: BuilderConfig,
  filename = `report-${Date.now()}.csv`,
): Promise<void> {
  return exportReport(organizationId, config, 'csv', filename);
}

// ----------------------------------------------------------------------------
// Saved reports (system templates + private org reports)
// ----------------------------------------------------------------------------

export async function listSavedReports(organizationId: string): Promise<SavedReport[]> {
  const r = await apiRequest<{ reports: SavedReport[]; count: number }>(
    '/reports/saved',
    { method: 'GET' },
    organizationId,
  );
  return r.reports;
}

export async function createSavedReport(organizationId: string, input: SavedReportInput): Promise<SavedReport> {
  return apiRequest<SavedReport>(
    '/reports/saved',
    { method: 'POST', body: JSON.stringify(input) },
    organizationId,
  );
}

export async function updateSavedReport(
  organizationId: string,
  id: string,
  patch: Partial<SavedReportInput>,
): Promise<SavedReport> {
  return apiRequest<SavedReport>(
    `/reports/saved/${id}`,
    { method: 'PATCH', body: JSON.stringify(patch) },
    organizationId,
  );
}

export async function deleteSavedReport(organizationId: string, id: string): Promise<void> {
  await apiRequest<{ ok: true }>(
    `/reports/saved/${id}`,
    { method: 'DELETE' },
    organizationId,
  );
}

// ----------------------------------------------------------------------------
// Run list (row-per-run view + drill-down)
//
// The response shape now lives in `@simcheck/shared-types`
// (`BuilderRunsQueryResponse` / `BuilderRunRow`). Kept alias for callers.
// ----------------------------------------------------------------------------

export type BuilderRunsResponse = BuilderRunsQueryResponse;

/**
 * List runs for a config. `dimValues` is optional: omit for the run-list view
 * (config with no grouping); pass a clicked row's dim values for the drill-down.
 */
export async function listBuilderRuns(
  organizationId: string,
  config: BuilderConfig,
  dimValues: Record<string, string | number | boolean | null> = {},
  timeZone: string = detectBrowserTimeZone(),
): Promise<BuilderRunsQueryResponse> {
  return apiRequest<BuilderRunsQueryResponse>(
    `/reports/query/runs?tz=${encodeURIComponent(timeZone)}`,
    {
      method: 'POST',
      body: JSON.stringify({ config, dimValues }),
    },
    organizationId,
  );
}
